import { useEffect, useRef, useCallback } from 'react';
import { notification, Button, Space } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import type { Workbox } from 'workbox-window';
import { swUpdateEvent } from '../../index';

const NOTIFICATION_KEY = 'sw-update';

/**
 * Service Worker 更新通知元件
 *
 * 掛載於 App 頂層，監聽 SW 更新事件，
 * 使用 Ant Design notification 顯示「新版本可用」提示。
 */
export default function UpdateNotification() {
  const wbRef = useRef<Workbox | null>(null);

  const handleUpdate = useCallback(() => {
    // 新 SW 已 skipWaiting 自動啟用並接管，直接重載即載入新版程式碼。
    // （messageSkipWaiting 對已啟用的 SW 為 no-op，controlling 事件也可能已提前觸發，故不倚賴它）
    window.location.reload();
  }, []);

  useEffect(() => {
    const handler = (event: Event) => {
      const { wb } = (event as CustomEvent).detail;
      wbRef.current = wb;

      notification.info({
        key: NOTIFICATION_KEY,
        message: '系統更新可用',
        description:
          '冷藏庫監控系統已有新版本，建議立即更新以獲得最新功能與修正。',
        placement: 'bottomRight',
        duration: 0,
        icon: <SyncOutlined spin style={{ color: 'var(--sw-primary)' }} />,
        btn: (
          <Space>
            <Button
              size="small"
              onClick={() => notification.destroy(NOTIFICATION_KEY)}
            >
              稍後
            </Button>
            <Button type="primary" size="small" onClick={handleUpdate}>
              立即更新
            </Button>
          </Space>
        ),
      });
    };

    swUpdateEvent.addEventListener('sw-update', handler);
    return () => {
      swUpdateEvent.removeEventListener('sw-update', handler);
      notification.destroy(NOTIFICATION_KEY);
    };
  }, [handleUpdate]);

  return null;
}
